import { Component, EventEmitter, OnInit, Output, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { GlobalStateService } from '@core/services/global.state.service';
import { UserBranchModel } from '@shared/models/user-Branch.model';
import { Router } from '@angular/router';
import { ApplicationListStateService } from 'app/modules/Application-View/application-list/application-list.state.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CmButtonModule } from '@shared/components/cm-button';
import { NewApplicationStartByStaff } from '@shared/models/requests/ApplicationResumeByStaff.request.dto';
import { UserManagementApiService } from '@core/services/api.services';
import { UserManagementStateService } from 'app/modules/setup/user-management/user-management.state.service';
import { AuthService } from '@core/services/auth';
import { ObservableInput, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-create-application-in-branch',
  templateUrl: './create-application-in-branch.component.html',
  styleUrl: './create-application-in-branch.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    CmButtonModule
  ]
})
export class CreateApplicationInBranchComponent implements OnInit, OnDestroy {
  userBranchOptions: { id: number; name: string; isPrimary: boolean; isAllowed: boolean }[] = [];
  defaultBranch: { id: number; name: string; isPrimary: boolean; isAllowed: boolean };
  form: FormGroup;
  isLoading = true;

  @Output() closeDialog: EventEmitter<void> = new EventEmitter<void>();
  private destroyed$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private globalState: GlobalStateService,
    private state: ApplicationListStateService,
    private auth: AuthService,
    private userService: UserManagementStateService,
    private dialogRef: MatDialogRef<CreateApplicationInBranchComponent>,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.formBuilder.group({
      applicationId: [null],
      branchId: [null],
    });
  }

  ngOnInit(): void {
    this.loadUserBranches();
  }

  private loadUserBranches(): void {
    this.isLoading = true;
    this.userService.getUserById(this.auth.userId);

    this.userService.user$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userData) => {
        if (!userData) {
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.userBranchOptions = userData.branches || [];
        this.defaultBranch = this.userBranchOptions.find(branch => branch.isPrimary);

        // Set default value if available
        if (this.defaultBranch) {
          this.form.patchValue({ branchId: this.defaultBranch.id });
        }
        this.isLoading = false;
        this.cdr.detectChanges();
        // If no branches loaded, try to reload after a short delay
        if (this.userBranchOptions.length === 0) {
          setTimeout(() => {
            console.log('No branches found, retrying...');
            this.userService.getUserById(this.auth.userId);
          }, 1000);
        }
      });
  }

  startApplication() {
    if (this.form.valid) {
      const branchId = this.form.get('branchId').value;
      const request: NewApplicationStartByStaff = {
        BranchId: branchId,
      };
      this.state.newApplicationStartByStaff(request).subscribe({
        next: (response: any) => {
          if (response?.result?.encryptedResponse) {
            const encryptedResponse = response.result.encryptedResponse;

            this.dialogRef.close();

            this.globalState.getSiteDetails().subscribe((data: any) => {
              const siteDomain = data?.mainSiteUrl.startsWith('http')
                ? data?.mainSiteUrl
                : `https://${data?.mainSiteUrl}`;

              const redirectUrl = `${siteDomain}/inBranch?encryptedResponse=${encodeURIComponent(encryptedResponse)}`;
              window.open(redirectUrl, '_blank');
            });
          }
        },
        error: (err) => {
          console.error('Error resuming application by staff:', err);
        }
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
